'use client';

import { useId } from 'react';
import type { SVGProps } from 'react';

/**
 * Glyphs for Pitchcrow-native documents. Unlike uploaded files (folded-corner
 * Tabler file icons), these are canvases — both on the same landscape frame,
 * a chart for decks and a grid for sheets — with the brand crow perched on the
 * top-right corner, sitting in a masked gap so it never fuses with the frame.
 * Color at the call site with text-doc-sheet / text-doc-deck.
 *
 * Same marks as marketing (`marketing/src/components/product/doc-icons.tsx`) —
 * change one, change the other.
 */

/** The wordmark crow, machine-traced from the 512px logo (46 points). */
const CROW =
  'M16.57 0.60L18.60 0.81L21.54 2.01L22.58 2.87L22.22 3.11L23.60 4.70L23.14 4.6' +
  '6L23.49 5.27L22.96 5.17L23.31 5.92L22.99 5.88L23.23 7.10L22.56 6.73L21.47 8.' +
  '27L17.92 10.73L18.87 9.07L17.65 10.07L16.46 10.69L18.02 8.81L16.33 9.85L15.4' +
  '3 10.14L17.41 8.26L15.93 8.92L14.93 9.06L17.08 7.61L15.14 7.91L16.47 7.27L17' +
  '.50 6.32L17.07 6.02L18.36 4.73L18.77 3.92L18.75 3.51L17.48 3.02L16.61 3.65L1' +
  '6.58 2.90L16.94 2.09L15.21 3.17L15.52 2.18L13.96 3.48L14.61 2.05L12.68 3.52L' +
  '13.49 2.39L12.34 2.75L11.40 2.77L12.64 2.35L14.74 1.13Z';

/**
 * The gap the crow sits in, cut out of whatever it overlaps. The id must be
 * unique per instance — a duplicate id can resolve to a hidden copy elsewhere
 * in the DOM, and a mask inside a hidden SVG computes empty and erases the
 * frame. Hence useId rather than a fixed id.
 */
function CrowCut({ id }: { id: string }) {
  return (
    <mask
      id={id}
      maskUnits="userSpaceOnUse"
      x="-4"
      y="-4"
      width="32"
      height="32"
    >
      <rect x="-4" y="-4" width="32" height="32" fill="#fff" />
      <path
        d={CROW}
        fill="#000"
        stroke="#000"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </mask>
  );
}

export function DeckDocIcon(props: SVGProps<SVGSVGElement>) {
  const id = useId();
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <CrowCut id={id} />
      <g mask={`url(#${id})`}>
        <rect
          x="2.5"
          y="6"
          width="18"
          height="14"
          rx="2.5"
          stroke="currentColor"
          strokeWidth="1.75"
        />
        <path
          d="M7.5 16.5V14M11.5 16.5v-5M15.5 16.5v-5.5"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </g>
      <path d={CROW} fill="currentColor" />
    </svg>
  );
}

export function SheetDocIcon(props: SVGProps<SVGSVGElement>) {
  const id = useId();
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <CrowCut id={id} />
      <g mask={`url(#${id})`}>
        <path
          d="M3.7 7.1h15.6c.6 0 1.1.5 1.1 1.1v2.3H2.6V8.2c0-.6.5-1.1 1.1-1.1Z"
          fill="currentColor"
          opacity=".38"
        />
        <rect
          x="2.5"
          y="6"
          width="18"
          height="14"
          rx="2.5"
          stroke="currentColor"
          strokeWidth="1.75"
        />
        <path
          d="M2.5 11.1h18M11.5 11.1V20M2.5 15.7h18"
          stroke="currentColor"
          strokeWidth="1.75"
        />
      </g>
      <path d={CROW} fill="currentColor" />
    </svg>
  );
}
